import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import MarqitLogoFull from '../components/MarqitLogoFull';
import LegalFooter from '../components/LegalFooter';
import { registration } from '../services/api';
import { ACCOUNT_STATUS, loginDenialMessage } from '../constants/accountStatus';


const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [denialReason, setDenialReason] = useState('');
  const [resent, setResent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setDenialReason('');
    setResent(false);
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(loginDenialMessage(result.reason, result.supportContact) || result.error);
      setDenialReason(result.reason || '');
    }

    setLoading(false);
  };

  const handleResend = async () => {
    try {
      await registration.resendVerification(email);
    } catch (err) {
      // The endpoint answers the same way regardless; nothing useful to show.
    }
    setResent(true);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(145deg, #EAFBFA 0%, #DFF7F6 40%, #f0fafa 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        p: 2,
        // Decorative blobs
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '40vw',
          height: '40vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(25,198,192,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-10%',
          left: '-5%',
          width: '35vw',
          height: '35vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(25,198,192,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      {/* Login card */}
      <Box
        sx={{
          background: '#fff',
          borderRadius: '28px',
          boxShadow: '0 24px 80px rgba(25,198,192,0.12), 0 4px 16px rgba(0,0,0,0.06)',
          border: '1px solid rgba(25,198,192,0.1)',
          p: { xs: 3, sm: 5 },
          width: '100%',
          maxWidth: 420,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Brand */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
          <Box sx={{ mb: 2.5 }}>
            <MarqitLogoFull width={210} />
          </Box>
          <Typography
            sx={{
              color: '#6B7280',
              fontSize: '0.9375rem',
              fontWeight: 500,
            }}
          >
            Вход в систему
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2.5,
              borderRadius: '12px',
              background: 'rgba(246,193,195,0.3)',
              border: '1px solid rgba(217,79,92,0.2)',
              color: '#D94F5C',
              '& .MuiAlert-icon': { color: '#D94F5C' },
            }}
          >
            {error}
            {denialReason === ACCOUNT_STATUS.EMAIL_UNVERIFIED && (
              <Box sx={{ mt: 1 }}>
                {resent ? (
                  <Typography sx={{ fontSize: '0.875rem' }}>
                    Письмо отправлено повторно — проверьте почту.
                  </Typography>
                ) : (
                  <Link
                    component="button"
                    type="button"
                    onClick={handleResend}
                    sx={{ fontSize: '0.875rem', color: '#D94F5C', fontWeight: 600 }}
                  >
                    Отправить письмо повторно
                  </Link>
                )}
              </Box>
            )}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            required
            fullWidth
            id="email"
            label="Email"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            required
            fullWidth
            name="password"
            label="Пароль"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              mt: 1,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 700,
              borderRadius: '14px',
              background: loading
                ? 'rgba(25,198,192,0.5)'
                : 'linear-gradient(135deg, #19C6C0 0%, #11BDB6 100%)',
              boxShadow: loading ? 'none' : '0 6px 20px rgba(25,198,192,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #11BDB6 0%, #0BABA5 100%)',
                boxShadow: '0 8px 24px rgba(25,198,192,0.5)',
              },
            }}
          >
            {loading ? 'Вход...' : 'Войти'}
          </Button>
          <Link component={RouterLink} to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`}
            sx={{ alignSelf: 'center', fontSize: '0.875rem', fontWeight: 600, color: '#11BDB6' }}>
            Забыли пароль?
          </Link>
        </Box>

        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '0.875rem', color: '#6B7280' }}>
          Нет аккаунта?{' '}
          <Link component={RouterLink} to="/register" sx={{ fontWeight: 600, color: '#11BDB6' }}>
            Зарегистрироваться
          </Link>
        </Typography>
      </Box>
      <LegalFooter sx={{ mt: 3, maxWidth: 520, position: 'relative', zIndex: 1 }} />
    </Box>
  );
};

export default LoginPage;
